package dev.kuml.portal.web.editor

/**
 * Connection-lifecycle state for the collaboration WebSocket -- see `CollabClient.kt`'s KDoc for
 * how the impure client drives this. Pure, DOM-free, so the "simple reconnect-or-give-up, no
 * exponential backoff" rule is directly unit-testable (see `CollabReconnectTest`).
 */
public sealed interface CollabPhase {
    public data object Connecting : CollabPhase

    public data object Live : CollabPhase

    public data class Reconnecting(
        val attempt: Int,
    ) : CollabPhase

    public data object GaveUp : CollabPhase
}

/**
 * Deliberately simple: exactly one retry after an UNINTENDED close (network blip, server restart,
 * idle-timeout), then give up quietly -- no exponential backoff, no offline queue, matching the
 * plan's v1 scope. An INTENTIONAL close (the user navigating away from the editor, see
 * `EditorScreen.kt`'s `EditorScreenHandle.dispose`) never reaches [afterUnintendedClose] at all --
 * the impure `CollabClient` checks its own `closingIntentionally` flag BEFORE ever consulting this
 * state machine, so there is no "intentional" branch here to test: the caller's gating is the whole
 * mechanism.
 */
public object CollabReconnect {
    public const val MAX_ATTEMPTS: Int = 1
    public const val RETRY_DELAY_MS: Int = 2000

    /**
     * The next [CollabPhase] after an unintended close, given the [current] phase. The very first
     * unintended close (from [CollabPhase.Connecting] or [CollabPhase.Live]) always starts a single
     * retry attempt ([CollabPhase.Reconnecting] with `attempt = 1`); a close while ALREADY
     * reconnecting either advances to the next attempt (if still under [MAX_ATTEMPTS]) or gives up
     * for good ([CollabPhase.GaveUp]) -- from which there is no further transition (quiet, no error
     * toast, matching "reconnect-or-give-up").
     */
    public fun afterUnintendedClose(current: CollabPhase): CollabPhase =
        when (current) {
            is CollabPhase.Reconnecting ->
                if (current.attempt < MAX_ATTEMPTS) CollabPhase.Reconnecting(current.attempt + 1) else CollabPhase.GaveUp
            else -> CollabPhase.Reconnecting(1)
        }
}
